/* eslint-disable max-lines */
import { action, computed, extendObservable, flow, observable, transaction } from 'mobx';

import { AnalyticsStore, ContextStore, ExperimentStore, PageStore, RouteStore, SerializedData, Store, timeoutPromise } from 'bernie-core';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { PageData } from 'bernie-http';

import { BexApiSource } from 'sources/bex-api-source';

import {
  adaptPropertySearch,
  getPropertySearchInput,
  getPropertySortAndFilterInputs,
  mapOmnitureData,
  mapPropertySearchListings,
} from 'stores/adapters';
import { adaptQueryToRateCalendarModel, getPropertySearchResultsVariables } from 'stores/adapters/bex-api/search/rate-calendar-adapter';
import { evaluateAndLogExperiment } from 'stores/utils';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  CarouselModel,
  DefaultRequestFields,
  HotelViewModel,
  LegalDisclaimer,
  LoyaltyEducation,
  LoyaltyMessagingModel,
  OverFilteredModel,
  PriceSummary,
  PrimaryPageMessagingModel,
  RewardsDialogModel,
  TravelAd,
} from 'models';
import { ExitIntentModel } from 'models/common/common-types';
import { MultiItemContext } from 'models/multi-item/multi-item-types';
import { PropertySearchListingViewModel, SearchAndFilterDisplayOptionsModel } from 'models/hotel/search-types';
import { ResultType } from 'constants/resultType';

import {
  LodgingPwaPropertySearch,
  LodgingPwaPropertySearchSortAndFilter,
  PaginationInput,
  Points,
  PropertyFields,
  SearchDialogMessage,
  SearchLinkMessage,
} from '__generated__/api';
import { interceptGraphQLResponse } from './dev-modification/intercept';
import { MessagingCardProps } from 'src/common/components/common/messaging-card/messaging-card.tsx';
import { OperationResult } from '__generated__/api/api-operations';
import { stringify } from 'query-string';

import NavigationTabs = LodgingPwaPropertySearch.NavigationTabs;

export class SearchResultsStore extends Store {
  public static HOTEL_RENDER_COUNT_SERVER_SIDE = 20;
  public static HOTEL_TOTAL_RENDER_COUNT = 50;

  // SRP_ReducedPayload_Perfomance_Test (38414)
  public static HOTEL_INITIAL_FETCH_COUNT = 50;

  private pendingSearchResultsFetchFlow;

  constructor(private bexApiSource: BexApiSource, state: SerializedData, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    this.searchCriteria = new SearchCriteriaModel();

    this.fetch = this.fetch.bind(this);
    this.searchProperties = this.searchProperties.bind(this);
  }

  public hydrate(data: SerializedData): void {
    delete data.source;
    delete data.bexApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    this.syncSearchDestination();
    delete data.searchCriteria;

    extendObservable(this, data);
  }

  public static DEFAULTS = {
    alternateDateFetchUrl: undefined,
    exitIntent: undefined,
    expandForm: false,
    foregoClientFetch: false,
    propertySearchListings: [],
    hotelsMaxDisplay: SearchResultsStore.HOTEL_RENDER_COUNT_SERVER_SIDE,
    insights: undefined,
    isLoading: true,
    lodgingPriceBreakDown: {},
    loyaltyMessagingModel: undefined,
    messages: [],
    resultSectionHeadings: [],
    omnitureData: {},
    overFilteredModel: undefined,
    pageData: {},
    primaryPageMessagingModel: undefined,
    searchAndFilterDisplayOptions: {},
    searchedDestination: '',
    shouldFetch: true,
    sortAndFilter: {},
    totalPropertyCount: 0,
    pageUsableMarked: false,
    carouselListings: [],
  };

  @observable
  public alternateDateFetchUrl: string;
  @observable
  public error: any;
  @observable
  public errors: any[];
  @observable
  public exitIntent: ExitIntentModel;
  @observable
  public legalDisclaimer: LegalDisclaimer;
  @observable
  public navigationTabs: NavigationTabs;
  @observable
  public expandForm: boolean; // Set on client.
  @observable
  public foregoClientFetch: boolean;
  @observable
  public propertySearchListings: PropertySearchListingViewModel[];
  @observable
  public hotelsMaxDisplay: number;
  @observable
  public isLoading: boolean;
  @observable
  public loyaltyEducation: LoyaltyEducation;
  @observable
  public loyaltyMessagingModel: LoyaltyMessagingModel;
  @observable
  public lodgingPriceBreakDown: PriceSummary;
  @observable
  public map: any;
  @observable
  public messages: MessagingCardProps[];
  @observable
  public resultSectionHeadings: LodgingPwaPropertySearch.ResultSectionHeadings[];
  @observable
  public omnitureData: any;
  @observable
  public overFiltered: boolean;
  @observable
  public overFilteredModel: OverFilteredModel;
  @observable
  public primaryPageMessagingModel: PrimaryPageMessagingModel;
  @observable
  public propertyTypeCarousel: CarouselModel;
  @observable
  public rewardsDialogContent: RewardsDialogModel;
  @observable
  public searchAndFilterDisplayOptions: SearchAndFilterDisplayOptionsModel; // Set on server.
  @observable
  public searchedOrigin: string;
  @observable
  public searchedDestination: string;
  @observable
  public shouldFetch: boolean;
  @observable
  public sortAndFilter: any;
  @observable
  public resultsSummary: (LodgingPwaPropertySearch.ResultsSummary & SearchLinkMessage.Fragment & SearchDialogMessage.Fragment)[];
  @observable
  public totalPropertyCount: number;
  @observable
  public carouselListings: CarouselModel[];
  @observable.ref
  public pageData: PageData;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;
  @observable
  public multiItemSearchContext: LodgingPwaPropertySearch.MultiItemSearchContext;
  @observable
  public errorResponse: LodgingPwaPropertySearch.ErrorResponse;

  public defaultRequestFields: DefaultRequestFields;
  public maxHotelsDisplayedPerLoad: number = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  public prefetchedMultiItemSession: boolean = false;
  public isReducedPayloadExperimentV1: boolean = false;

  @observable
  public pageUsableMarked: boolean;
  private searchFetched: boolean = false;

  @computed
  get isError(): boolean {
    return Boolean(!this.isLoading && (this.error || this.errors || this.overFiltered || this.errorResponse));
  }

  @computed
  get hasAtLeastOneProperty(): boolean {
    return this.propertySearchListings?.length > 0;
  }

  @computed
  get isEmpty(): boolean {
    return Boolean(!this.isLoading && !this.hasAtLeastOneProperty);
  }

  @computed
  get properties(): HotelViewModel[] {
    return this.propertySearchListings
      .filter(listing => listing && listing.resultType === ResultType.HOTEL)
      .map(listing => listing.model as HotelViewModel); // This is wrong.
  }

  @computed
  get propertyListings(): PropertyFields.Fragment[] {
    return this.propertySearchListings
      .filter(listing => listing && listing.resultType === ResultType.HOTEL)
      .map(listing => listing.model as PropertyFields.Fragment);
  }

  @action
  public showMoreHotels(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    multiItem: MultiItemStore,
    experiments: ExperimentStore
  ): Promise<number> {
    this.isLoading = true;

    return new Promise(resolve => {
      setTimeout(
        action(() => {
          const currentMax = this.hotelsMaxDisplay;
          const numHotelsFetched = this.properties.length;

          if (this.hotelsMaxDisplay < numHotelsFetched) {
            this.hotelsMaxDisplay += this.maxHotelsDisplayedPerLoad;
            resolve(currentMax);
          }

          if (
            this.hotelsMaxDisplay + this.maxHotelsDisplayedPerLoad >= numHotelsFetched && // is one batch away from the end
            numHotelsFetched < this.totalPropertyCount
          ) {
            this.fetchNextBatch(analyticsStore, context, numHotelsFetched, this.maxHotelsDisplayedPerLoad, multiItem, resolve, experiments);
          } else {
            this.isLoading = false;
            resolve(currentMax);
          }
        }),
        400
      );
    });
  }

  private fetchNextBatch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    startingIndex: number,
    numToFetch: number,
    multiItem: MultiItemStore,
    showMorePromiseResolve: () => void,
    experiments?: ExperimentStore
  ): void {
    const multiItemContext = multiItem ? multiItem.multiItemContext : null;

    const propertySearchFetch = this.searchProperties(
      context,
      multiItemContext,
      this.searchCriteria,
      this.pageData.pageId,
      {
        size: numToFetch,
        startingIndex,
      },
      experiments
    );
    propertySearchFetch
      .then(({ data, errors, extensions }) => {
        transaction(() => {
          this.errors = errors;
          // isLoading needs to change with new omniture data in one step
          this.isLoading = false;
          this.propertySearchListings = this.propertySearchListings.concat(mapPropertySearchListings(data));
          this.omnitureData = mapOmnitureData(extensions.analytics);

          if (multiItem) {
            multiItem.updateSessionId(this.getMultiItemId(data));
          }
          showMorePromiseResolve();
        });
      })
      .catch(error => {
        this.isLoading = false;
        this.logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_FETCH_ERROR, getErrorString(error));
      });

    analyticsStore.publishClientSidePayload([this.omnitureData]);
  }

  @action
  public updateSearchCriteria(searchCriteria): void {
    this.searchCriteria = searchCriteria;
    this.propertyTypeCarousel = undefined;
    this.syncSearchDestination();
    this.shouldFetch = true;
  }

  @action
  public createSessionBeforeMultiItemFetch(
    multiItem: MultiItemStore,
    context: ContextStore,
    incomingSearchCriteria: SearchCriteriaModel
  ): Promise<any> {
    this.isLoading = true;
    this.shouldFetch = false;
    this.propertySearchListings = [];
    this.prefetchedMultiItemSession = true;

    return multiItem
      .createMultiItemSession(context, incomingSearchCriteria)
      .then(misId => (incomingSearchCriteria.misId = misId))
      .catch(() => {
        this.isLoading = false;
        this.shouldFetch = true;
        this.prefetchedMultiItemSession = false;
        this.error = true;
      });
  }

  @computed
  get usable(): boolean {
    return this.searchFetched;
  }

  @action
  public fetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    timeout: number,
    additionalCriteria: SerializedData,
    multiItem: MultiItemStore,
    page: PageStore,
    experiments?: ExperimentStore
  ): Promise<any> {
    if (this.shouldFetch === false && !this.prefetchedMultiItemSession) {
      return Promise.resolve(this);
    }
    // cancel previous search fetch promise to prevent a race condition
    this.pendingSearchResultsFetchFlow?.cancel();
    this.pendingSearchResultsFetchFlow = this.createSearchResultsFetchFlow(
      analyticsStore,
      context,
      timeout,
      additionalCriteria,
      multiItem,
      page,
      experiments
    );

    return this.pendingSearchResultsFetchFlow;
  }

  /*
   * The flow wrapper is an optional alternative to async / await that makes it easier
   * to work with MobX actions and allows the promise returned to be cancelled.
   * https://mobx.js.org/actions.html#using-flow-instead-of-async--await-
   */
  @action
  private createSearchResultsFetchFlow = flow(
    function*(
      analyticsStore: AnalyticsStore,
      context: ContextStore,
      timeout: number,
      additionalCriteria: SerializedData = {},
      multiItem: MultiItemStore,
      page: PageStore,
      experiments?: ExperimentStore
    ) {
      this.prefetchedMultiItemSession = false;
      this.shouldFetch = false;
      this.isLoading = true;
      this.error = null;
      this.errors = [];
      this.propertySearchListings = [];
      this.insights = undefined;
      this.messages = [];
      this.carouselListings = [];
      this.isReducedPayloadExperimentV1 = experiments?.exposures?.SRP_ReducedPayload_Perfomance_Test?.bucket === 1;
      this.isSRPStandaloneQueryExperiment = experiments?.exposures?.SRP_STANDALONE_PROPERTYSEARCH?.bucket === 1;

      const searchPagination = this.isReducedPayloadExperimentV1
        ? {
            size: SearchResultsStore.HOTEL_INITIAL_FETCH_COUNT,
            startingIndex: 0,
          }
        : null;

      this.hotelsMaxDisplay = SearchResultsStore.HOTEL_RENDER_COUNT_SERVER_SIDE;

      const multiItemContext = multiItem ? multiItem.multiItemContext : null;

      const criteriaJson = this.searchCriteria.toJSON();
      const bexApiCriteria = { ...criteriaJson, ...additionalCriteria };

      const propertySearchFetch = timeoutPromise(
        this.searchProperties(context, multiItemContext, bexApiCriteria, page.pageId, searchPagination, experiments),
        timeout,
        'propertySearchFetch timeout error'
      );

      const parallelSortAndFilter = experiments?.exposures?.LShop_SRP_Delayed_Sort_And_Filter?.bucket === 1;
      const sortAndFilterFetch = parallelSortAndFilter
        ? timeoutPromise(this.fetchSortAndFilter(context, multiItem, experiments), timeout, 'sortAndFilterFetch timeout error')
        : Promise.resolve();

      try {
        const [bexApiResults, bexApiSortAndFilterResults] = yield Promise.all([propertySearchFetch, sortAndFilterFetch]);

        if (multiItem && bexApiResults) {
          multiItem.updateSessionId(this.getMultiItemId(bexApiResults.data));
        }
        // update useRewards based on the default value used in Bex api
        if (this.searchCriteria.useRewards === '') {
          this.searchCriteria.useRewards = SearchResultsStore.GetDefaultUseRewards(bexApiResults);
        }

        this.errors = bexApiResults.errors;

        const data = adaptPropertySearch(bexApiResults, experiments, bexApiSortAndFilterResults?.data?.propertySearch?.sortAndFilter);

        this.isLoading = false;
        this.pendingSearchResultsFetchFlow = null;

        if (data) {
          this.setOverFilteredModel(data.overFilteredModel);

          const searchCriteria = this.searchCriteria.clone();
          const defaultRequestFields = data.defaultRequestFields;

          if (defaultRequestFields) {
            Object.assign(searchCriteria, defaultRequestFields);
          }

          const sortAndFilterSelected = data.sortAndFilter && data.sortAndFilter.selected;
          if (sortAndFilterSelected) {
            Object.assign(searchCriteria, sortAndFilterSelected);
          }

          if (data.exitIntent) {
            this.exitIntent = data.exitIntent;
          }

          if (data.legalDisclaimer) {
            this.legalDisclaimer = data.legalDisclaimer;
          }

          if (data.navigationTabs) {
            this.navigationTabs = data.navigationTabs;
          }

          this.searchCriteria = searchCriteria;

          Object.assign(this, data);

          analyticsStore.publishClientSidePayload([this.omnitureData]);
        }

        this.searchFetched = true;

        return data;
      } catch (err) {
        this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, err);
        // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
        this.error = true;
        this.isLoading = false;
        this.shouldFetch = true;

        // recover and pass object with error details
        return { error: err };
      }
    }.bind(this)
  );

  private searchProperties(
    context: ContextStore,
    multiItemContext: MultiItemContext,
    combinedCriteria: any,
    pageId: string,
    searchPagination?: PaginationInput,
    experiments?: ExperimentStore
  ): Promise<OperationResult<LodgingPwaPropertySearch.Query>> {
    const variables: LodgingPwaPropertySearch.Variables = getPropertySearchInput(
      context,
      combinedCriteria,
      this.sortAndFilter,
      multiItemContext,
      false,
      searchPagination,
      experiments,
      pageId
    );

    return this.bexApiSource.searchProperties(variables, pageId).then(interceptGraphQLResponse);
  }

  private fetchSortAndFilter(
    context: ContextStore,
    multiItem: MultiItemStore,
    experiments: ExperimentStore
  ): Promise<OperationResult<LodgingPwaPropertySearchSortAndFilter.Query>> {
    const variables: LodgingPwaPropertySearchSortAndFilter.Variables = getPropertySortAndFilterInputs(
      context,
      this.searchCriteria,
      this.sortAndFilter,
      multiItem ? multiItem.multiItemContext : null,
      false,
      null,
      experiments
    );

    return this.bexApiSource.fetchSortAndFilter(variables, this.pageData.pageId).then(interceptGraphQLResponse);
  }

  private syncSearchDestination() {
    if (this.searchCriteria.destination && this.searchCriteria.destination !== '') {
      this.searchedDestination = this.searchCriteria.destination;
    } else if (this.searchedDestination && this.searchedDestination !== '') {
      this.searchCriteria.destination = this.searchedDestination;
    }
  }

  private logErrorEvent(event, err) {
    if (this.logger) {
      this.logger.logEvent(event, getErrorString(err));
    }
  }

  private setOverFilteredModel(overFilteredModel: OverFilteredModel) {
    if (this.overFilteredModel && !overFilteredModel) {
      this.overFilteredModel = null;
    }
  }

  public findProperty(hotelId: string, travelAd: TravelAd): PropertyFields.Fragment {
    return this.propertyListings.find(listing => listing.id === hotelId && (listing.sponsoredListing === null) === (travelAd === null));
  }

  @action
  public displaySearchButton() {
    this.map = Object.assign({}, this.map, {
      showSearchButton: true,
    });
  }

  @action
  public dismissMapMessage() {
    this.map = Object.assign({}, this.map, {
      mapMessage: '',
    });
  }

  @action
  public clearDefaultRequestFields() {
    this.defaultRequestFields = {};
  }

  @action
  public setHotelsMaxDisplayToTotalRenderCount() {
    this.hotelsMaxDisplay = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  }

  @action
  public clearFilters(routeStore: RouteStore) {
    if (routeStore && typeof routeStore.updateSearch === 'function' && this.sortAndFilter && this.sortAndFilter.pills) {
      const currentParams = { ...routeStore.queryParams };
      this.sortAndFilter.pills.forEach(pill => {
        currentParams[pill.name] = this.sortAndFilter.defaults?.[pill.name] || '';
      });
      routeStore.updateSearch(stringify(currentParams));
    }
  }

  @action
  public setExpandForm(shouldExpandForm: boolean) {
    this.expandForm = shouldExpandForm;
  }

  public getPageLoadNumber() {
    return this.hotelsMaxDisplay / this.maxHotelsDisplayedPerLoad;
  }

  private getMultiItemId(data: LodgingPwaPropertySearch.Query) {
    return data?.propertySearch?.shoppingContext?.multiItem?.id;
  }

  public static GetDefaultUseRewards(bexApiResults: any): string {
    const defaultSwp = bexApiResults?.data?.propertySearch?.searchCriteria?.propertyShopOptions?.points;
    if (Points.SHOP_WITHOUT_POINTS === defaultSwp) {
      return 'false';
    }

    return 'true';
  }

  @action
  public fetchRateCalendar(context: ContextStore, experiments: ExperimentStore) {
    const experimentName = 'Destination_Pricing_Rate_Calendar_HTG_Filter_Wizard';
    const bucket = evaluateAndLogExperiment(experimentName, context, experiments, this.pageData);

    if (!bucket || bucket === 0) {
      return Promise.resolve();
    }

    try {
      const variables = getPropertySearchResultsVariables(context, this.searchCriteria, this.properties);

      return this.bexApiSource
        .fetchRateCalendar(variables, this.pageData.pageId)
        .then(adaptQueryToRateCalendarModel)
        .catch(error => {
          this.logErrorEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_RATE_CALENDAR_FETCH_ERROR, error);

          return {};
        });
    } catch (error) {
      this.logErrorEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_RATE_CALENDAR_FETCH_ERROR, error);

      return Promise.resolve();
    }
  }
}
